import React, { useEffect, useMemo, useState } from 'react';

import { ErrorPrompt } from '@opendoor/bricks/complex';
import { Button, Divider, Flex, Text } from '@opendoor/bricks/core';

import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';
import { usePrevious } from 'shared/helpers/hooks/usePrevious';

import { AnimationWrapper } from 'buying/components/AnimationWrapper';
import { ContactForm } from 'buying/components/buyDirectCheckout/ContactForm';
import { ContingentSale } from 'buying/components/buyDirectCheckout/ContingentSale';
import { WorkingWithAgent } from 'buying/components/buyDirectCheckout/WorkingWithAgent';
import {
  ContactDetail,
  validateContactDetails,
} from 'buying/stateMachines/buyDirectCheckoutFlowStateMachine';

const PRIMARY_BUYER_KEY = 0;
const COBUYER_KEY = 1;

interface IContactDetailsProps {
  buyerDetails?: Record<string, ContactDetail>;
  hasOwnAgent?: 'yes' | 'no';
  isContingentSale?: 'yes' | 'no';
  send: (
    args:
      | string
      | { type: string; value: string | boolean | Record<string, ContactDetail> | number },
  ) => void;
}

/**
 * Contact Details
 * storybook-check-ignore
 */
export const ContactDetails: React.FC<IContactDetailsProps> = ({
  hasOwnAgent,
  isContingentSale,
  send,
  buyerDetails,
}) => {
  const { isMobile } = useMobileDevice();
  const [contactDetails, setContactDetails] = useState<Record<string, ContactDetail>>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const prevBuyerDetails = usePrevious(buyerDetails);
  const [shouldAddCoBuyer, setShouldAddCoBuyer] = useState<boolean>(false);

  useEffect(() => {
    // buyerDetails is set asynchronously so we need to hydrate state once the initial fetch is complete
    if (!prevBuyerDetails && buyerDetails) {
      setContactDetails(buyerDetails);
    }
  }, [prevBuyerDetails, buyerDetails]);

  // On "BACK"
  useEffect(() => {
    const hasCobuyer = Object.keys(contactDetails).length === 2;
    if (hasCobuyer) {
      setShouldAddCoBuyer(true);
    }
  }, [contactDetails]);

  const handleCoBuyerToggle = () => {
    if (shouldAddCoBuyer) {
      setShouldAddCoBuyer(false);
      setContactDetails((prevState) => {
        // retrieve both keys and values from the previous state object
        const { [1]: coBuyer, ...buyer } = prevState;
        // and discard the second key and value since we're removing the co-buyer
        return {
          ...buyer,
        };
      });
    } else {
      setShouldAddCoBuyer(true);
    }
  };

  const handleOnFormFieldChange = (
    key: string,
    field: string,
    value: string | number | boolean,
  ) => {
    setContactDetails((prevState) => {
      return {
        ...prevState,
        [key]: {
          ...contactDetails?.[key],
          id: key,
          [field]: value,
        },
      };
    });
  };

  const handleOnAgentChange = (selection: string) => {
    send({ type: 'setOwnAgent', value: selection });
  };

  const handleOnContingentSaleChange = (selection: string) => {
    send({ type: 'setContingentSale', value: selection });
  };

  // Validate the form fields before moving forward
  const handleOnNextClick = () => {
    const formValidationError = validateContactDetails(contactDetails);
    if (formValidationError) {
      setErrorMessage(formValidationError);
      return window.scrollTo(0, 0);
    }
    send({ type: 'setContactDetails', value: contactDetails });
    handleForward();
  };

  const handleForward = () => {
    send('FORWARD');
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    send('BACK');
    window.scrollTo(0, 0);
  };

  const isButtonDisabled = useMemo(() => {
    return hasOwnAgent === undefined || isContingentSale === undefined;
  }, [hasOwnAgent, isContingentSale]);

  return (
    <AnimationWrapper>
      <Flex flexDirection="column" alignItems="flex-start" width="100%">
        <Text size={isMobile ? '160' : '150'} fontWeight="medium" mb={7}>
          Contact details
        </Text>
        <ErrorPrompt message={errorMessage} />
        <ContactForm
          contactDetails={buyerDetails}
          index={PRIMARY_BUYER_KEY}
          onFormChange={handleOnFormFieldChange}
        />
        {shouldAddCoBuyer && (
          <ContactForm
            contactDetails={buyerDetails}
            index={COBUYER_KEY}
            onFormChange={handleOnFormFieldChange}
          />
        )}
        <Button
          alignSelf="flex-start"
          analyticsName="buy-direct-cobuyer"
          aria-label={!shouldAddCoBuyer ? 'Add co-buyer' : 'Remove co-buyer'}
          onClick={handleCoBuyerToggle}
          variant="link"
        >
          {!shouldAddCoBuyer ? 'Add co-buyer' : 'Remove co-buyer'}
        </Button>
        <Divider mt={8} mb={8} width="100%" />
        <WorkingWithAgent onAgentChange={handleOnAgentChange} hasOwnAgent={hasOwnAgent} />
        <Divider mt={8} mb={8} width="100%" />
        <ContingentSale
          isContingentSale={isContingentSale}
          onContingentSaleChange={handleOnContingentSaleChange}
        />
      </Flex>
      <Flex
        flexDirection={['column', null, 'row-reverse']}
        alignItems={['center', null, 'flex-end']}
        justifyContent="end"
        mt={8}
        width="100%"
      >
        <Button
          analyticsName="buy-direct-checkout-how-you-plan-to-buy"
          aria-label="How you plan to buy"
          data-testid="buy-direct-checkout-how-you-plan-to-buy-button"
          disabled={isButtonDisabled}
          mt={8}
          onClick={handleOnNextClick}
          variant="primary"
          width={['100%', '100%', '100%', '35%']}
          ml={[null, null, 4]}
        >
          How you plan to buy
        </Button>
        <Button
          analyticsName="buy-direct-checkout-how-you-plan-to-buy-back"
          aria-label="Back"
          data-testid="buy-direct-checkout-how-you-plan-to-buy-back-button"
          width={['100%', '100%', '100%', '35%']}
          variant="secondary"
          onClick={handleBack}
          mt={7}
          mr={[null, null, 4]}
        >
          Back
        </Button>
      </Flex>
    </AnimationWrapper>
  );
};
